import { clamp, rotateAroundEuler, Vec3 } from '../math/Vec3';
import type { FilamentSegment, ProjectionFigure } from '../simulation/types';

export interface ProjectionTarget {
  position: Vec3;
  meshId: number;
  filamentId: number;
}

function normalizedPoint(point: Vec3, cubeSize: Vec3): Vec3 {
  return new Vec3(
    point.x * cubeSize.x * 0.38,
    point.y * cubeSize.y * 0.38,
    point.z * cubeSize.z * 0.38,
  );
}

function sampleHeart(count: number, cubeSize: Vec3): Vec3[] {
  return Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    const x = (16 * Math.sin(angle) ** 3) / 17;
    const y =
      (13 * Math.cos(angle) - 5 * Math.cos(2 * angle) - 2 * Math.cos(3 * angle) - Math.cos(4 * angle) + 2) /
      17;
    return normalizedPoint(new Vec3(x, y, 0), cubeSize);
  });
}

function sampleSphere(count: number, cubeSize: Vec3): Vec3[] {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: count }, (_, index) => {
    const y = 1 - (2 * (index + 0.5)) / count;
    const radial = Math.sqrt(Math.max(0, 1 - y * y));
    const angle = index * goldenAngle;
    return normalizedPoint(new Vec3(Math.cos(angle) * radial, y, Math.sin(angle) * radial), cubeSize);
  });
}

function sampleCube(count: number, cubeSize: Vec3): Vec3[] {
  const corners = [
    new Vec3(-1, -1, -1),
    new Vec3(1, -1, -1),
    new Vec3(1, 1, -1),
    new Vec3(-1, 1, -1),
    new Vec3(-1, -1, 1),
    new Vec3(1, -1, 1),
    new Vec3(1, 1, 1),
    new Vec3(-1, 1, 1),
  ];
  const edges: readonly [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
  ];
  return Array.from({ length: count }, (_, index) => {
    const edge = edges[index % edges.length];
    if (!edge) return new Vec3();
    const cycle = Math.floor(index / edges.length);
    const cycles = Math.ceil(count / edges.length);
    const fraction = (cycle + 0.5) / cycles;
    const start = corners[edge[0]];
    const end = corners[edge[1]];
    return start && end ? normalizedPoint(start.lerp(end, fraction), cubeSize) : new Vec3();
  });
}

function sampleHelix(count: number, cubeSize: Vec3): Vec3[] {
  return Array.from({ length: count }, (_, index) => {
    const fraction = count === 1 ? 0.5 : index / (count - 1);
    const angle = fraction * Math.PI * 5;
    return normalizedPoint(
      new Vec3(Math.cos(angle) * 0.72, fraction * 2 - 1, Math.sin(angle) * 0.72),
      cubeSize,
    );
  });
}

function sampleStar(count: number, cubeSize: Vec3): Vec3[] {
  const vertices = Array.from({ length: 10 }, (_, index) => {
    const angle = Math.PI / 2 + (index * Math.PI) / 5;
    const radius = index % 2 === 0 ? 1 : 0.43;
    return new Vec3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
  });
  return Array.from({ length: count }, (_, index) => {
    const edgeProgress = (index / count) * vertices.length;
    const edgeIndex = Math.floor(edgeProgress) % vertices.length;
    const start = vertices[edgeIndex];
    const end = vertices[(edgeIndex + 1) % vertices.length];
    if (!start || !end) return new Vec3();
    const point = start.lerp(end, edgeProgress - Math.floor(edgeProgress));
    return normalizedPoint(new Vec3(point.x, point.y, 0), cubeSize);
  });
}

function sampleTorus(count: number, cubeSize: Vec3): Vec3[] {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: count }, (_, index) => {
    const majorAngle = index * goldenAngle;
    const minorAngle = (index / count) * Math.PI * 2 * Math.max(3, Math.round(Math.sqrt(count) / 2));
    const majorRadius = 0.66;
    const minorRadius = 0.27;
    const ring = majorRadius + minorRadius * Math.cos(minorAngle);
    return normalizedPoint(
      new Vec3(Math.cos(majorAngle) * ring, Math.sin(minorAngle) * minorRadius, Math.sin(majorAngle) * ring),
      cubeSize,
    );
  });
}

function sampleDna(count: number, cubeSize: Vec3): Vec3[] {
  return Array.from({ length: count }, (_, index) => {
    const pairIndex = Math.floor(index / 2);
    const pairCount = Math.max(1, Math.ceil(count / 2));
    const fraction = pairCount === 1 ? 0.5 : pairIndex / (pairCount - 1);
    const strandOffset = index % 2 === 0 ? 0 : Math.PI;
    const angle = fraction * Math.PI * 6 + strandOffset;
    return normalizedPoint(
      new Vec3(Math.cos(angle) * 0.58, fraction * 2 - 1, Math.sin(angle) * 0.58),
      cubeSize,
    );
  });
}

function samplePyramid(count: number, cubeSize: Vec3): Vec3[] {
  const vertices = [
    new Vec3(-0.9, -0.85, -0.9),
    new Vec3(0.9, -0.85, -0.9),
    new Vec3(0.9, -0.85, 0.9),
    new Vec3(-0.9, -0.85, 0.9),
    new Vec3(0, 0.95, 0),
  ];
  const edges: readonly [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [0, 4], [1, 4], [2, 4], [3, 4],
  ];
  return Array.from({ length: count }, (_, index) => {
    const edgeProgress = (index / count) * edges.length;
    const edge = edges[Math.floor(edgeProgress) % edges.length];
    if (!edge) return new Vec3();
    const start = vertices[edge[0]];
    const end = vertices[edge[1]];
    return start && end
      ? normalizedPoint(start.lerp(end, edgeProgress - Math.floor(edgeProgress)), cubeSize)
      : new Vec3();
  });
}

export function sampleProjectionFigure(
  figure: ProjectionFigure,
  cubeSize: Vec3,
  count = 120,
  rotation = new Vec3(),
  pivot = new Vec3(),
): Vec3[] {
  const sampleCount = Math.max(12, Math.round(count));
  let points: Vec3[];
  switch (figure) {
    case 'HEART':
      points = sampleHeart(sampleCount, cubeSize);
      break;
    case 'SPHERE':
      points = sampleSphere(sampleCount, cubeSize);
      break;
    case 'CUBE':
      points = sampleCube(sampleCount, cubeSize);
      break;
    case 'HELIX':
      points = sampleHelix(sampleCount, cubeSize);
      break;
    case 'STAR':
      points = sampleStar(sampleCount, cubeSize);
      break;
    case 'TORUS':
      points = sampleTorus(sampleCount, cubeSize);
      break;
    case 'DNA':
      points = sampleDna(sampleCount, cubeSize);
      break;
    case 'PYRAMID':
      points = samplePyramid(sampleCount, cubeSize);
      break;
  }
  if (
    Math.abs(rotation.x) <= Number.EPSILON &&
    Math.abs(rotation.y) <= Number.EPSILON &&
    Math.abs(rotation.z) <= Number.EPSILON
  ) return points;
  return points.map((point) => rotateAroundEuler(point, rotation, pivot));
}

function closestTarget(point: Vec3, segments: readonly FilamentSegment[]): ProjectionTarget | null {
  let best: ProjectionTarget | null = null;
  let bestDistanceSquared = Number.POSITIVE_INFINITY;
  for (const segment of segments) {
    const delta = segment.end.sub(segment.start);
    const lengthSquared = delta.lengthSquared();
    if (lengthSquared <= Number.EPSILON) continue;
    const fraction = clamp(point.sub(segment.start).dot(delta) / lengthSquared, 0.06, 0.94);
    const position = segment.start.lerp(segment.end, fraction);
    const distanceSquared = point.sub(position).lengthSquared();
    if (distanceSquared >= bestDistanceSquared) continue;
    bestDistanceSquared = distanceSquared;
    best = { position, meshId: segment.meshId, filamentId: segment.id };
  }
  return best;
}

export function mapFigureToFilaments(
  figure: ProjectionFigure,
  cubeSize: Vec3,
  segments: readonly FilamentSegment[],
  sampleCount = 120,
  rotation = new Vec3(),
  pivot = new Vec3(),
): ProjectionTarget[] {
  if (segments.length === 0) return [];
  return sampleProjectionFigure(figure, cubeSize, sampleCount, rotation, pivot)
    .map((point) => closestTarget(point, segments))
    .filter((target): target is ProjectionTarget => target !== null);
}
